import { useEffect } from 'react';
import { create } from 'zustand';
import { type ActiveCohortSession, getActiveCohortFeed } from '../services/circles.service';
import { getEcho, isWsEnabled } from '../services/echo';
import type { AvailabilityNotice, IncomingCall, MissedCall, PendingRing } from '../types/ring';
import type { StartingSoon } from '../types/startingSoon';
import { useAuthStore } from './auth.store';
import { useEmpathyQueueStore } from './empathy-queue.store';
import { useIncomingCallStore } from './incoming-call.store';
import { useRingStore } from './ring.store';
import { registerUserScopedReset } from './session-read';

/**
 * The cohort circles running right now, fetched ONCE however many things ask.
 *
 * Two consumers want this list and want different things from it:
 *
 *   - CircleStartBanner announces one, and stops once you leave it
 *   - "Your Groups" offers a way back in, for the whole circle, leave or not
 *
 * They used to poll independently — the banner is mounted globally in the
 * router and the hub renders inside it, so /app/empathy hit
 * /circles/active-cohort-sessions twice every 30 seconds for identical data.
 * The endpoint is not cheap (a whereHas plus a correlated whereExists over
 * circle_participants), and two independent timers could disagree for up to a
 * full interval.
 *
 * One poll, refcounted: it starts when the first consumer mounts and stops
 * when the last unmounts, so no timer runs on pages nobody is watching from.
 *
 * Two things a module-level shared poll has to get right, both of which this
 * one got wrong first (Codex, #797):
 *
 *  1. Every loop needs to know whether it is still the live one. Without that,
 *     a consumer unmounting while a request is in flight and another mounting
 *     before it resolves leaves TWO loops re-arming, and only the newest timer
 *     handle is reachable to clear. React StrictMode's double setup/cleanup
 *     hits this on the first render in dev.
 *
 *  2. Rows are identity-scoped. The store outlives any one session, so logging
 *     out or switching accounts without a reload has to drop them, and a
 *     response from the previous identity must not land afterwards.
 *
 * Both are handled by `generation`: it increments whenever the current loop
 * and any in-flight response stop being valid, and everything checks it before
 * re-arming or writing.
 */
export const POLL_MS = 30_000;

/**
 * P7d: the same poll carries the listener's side — the in-app ring and the
 * "You're available" notice. No second poll; the cadence tightens instead:
 * every RING_POLL_MS while a ring is up (so a ring someone else took, or a
 * seeker who left, closes within seconds), every AVAILABLE_POLL_MS while the
 * server says this person is inside their listening hours (so a ring shows
 * soon after the ladder sends it), POLL_MS otherwise.
 */
export const RING_POLL_MS = 5_000;
export const AVAILABLE_POLL_MS = 10_000;

/**
 * The cadence reads these stores, and nothing else outside the feed's own
 * fields. While the poll runs it watches every one of them (see
 * reconsiderCadence), so a store read added to nextPollDelay has to be added
 * here too, or a change in it waits out whatever timer is already armed.
 */
export function nextPollDelay(state: {
  ring: PendingRing | null;
  availableNow: boolean;
  incomingCall?: IncomingCall | null;
}): number {
  // A ring this device already answered or dismissed is not "up" (S10).
  if (state.ring && !useRingStore.getState().handled[state.ring.id]) return RING_POLL_MS;
  // R11: an incoming call on screen closes within seconds once it is voided
  // or reached elsewhere.
  if (state.incomingCall && !useIncomingCallStore.getState().handled[state.incomingCall.session_id])
    return RING_POLL_MS;
  if (state.availableNow) return AVAILABLE_POLL_MS;
  // R11: a seeker with a request up (WAITING: a group has it; MATCHING: the
  // ladder) hears about a claim within one short poll, on any page.
  const seeking = useEmpathyQueueStore.getState().seekerState;
  if (seeking === 'WAITING' || seeking === 'MATCHING') return AVAILABLE_POLL_MS;
  return POLL_MS;
}

interface ActiveCohortSessionsState {
  sessions: ActiveCohortSession[];
  /** P7d: the ring the server says is ringing for this person, or null. */
  ring: PendingRing | null;
  /** P7d: today's "You're available" notice, or null. */
  notice: AvailabilityNotice | null;
  /** P7d: inside listening hours right now (the ladder would ring them). */
  availableNow: boolean;
  /** R11: a call waiting for this person as the seeker, or null. */
  incomingCall: IncomingCall | null;
  /**
   * R14: the caller gave up ("Blake couldn't wait"). The server sends it ONCE
   * (the poll that carries it clears it), so it is latched here until the
   * person closes the card (dismissMissed), not overwritten by the next poll.
   */
  missedCall: MissedCall | null;
  /**
   * #1002 review 1: the sessions whose card was closed here. The server
   * carries a missed call for a minute after its first delivery (a lost
   * response, a second device), so a closed one must not come back.
   */
  missedDismissed: Record<number, true>;
  dismissMissed: () => void;
  /** The circle-starting-soon notice the server offers right now, or null. */
  startingSoon: StartingSoon | null;
  /**
   * server_time minus the device clock when that response landed (ms): the
   * notice counts down and expires on the server's clock (N4, S7).
   */
  serverClockOffsetMs: number;
  /** Refresh now, outside the poll cadence (e.g. after leaving a circle). */
  refresh: () => Promise<void>;
  /**
   * RING-PUSH: poll NOW and restart the cadence from here (a call push was
   * tapped or arrived in the foreground). With the poll running, the armed
   * timer (up to POLL_MS away) is dropped and a fresh loop ticks at once, so
   * the sheet shows within one round trip and the short cadence follows.
   * With no poll running (not signed in yet, a live session page), a plain
   * refresh: the poll's own first tick, when it starts, is immediate.
   */
  pollNow: () => void;
  /** Drop everything. Called on logout via auth.store's reset registry. */
  reset: () => void;
}

let subscribers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let generation = 0;
/** When the armed timer is due (epoch ms). */
let armedDeadline = 0;
/**
 * When the newest fetch that ended in the live generation was SENT: what
 * the feed's state is fresh as of (a failed one counts as the attempt). A moved deadline counts from here, not
 * from when the response landed (#1021 review 5: on a slow link the landing
 * is seconds later, and "one short poll" would stretch by the round trip).
 * An out-of-band refresh() counts too: its rows are as fresh as a tick's.
 */
let lastFetchStartedAt = 0;
/**
 * #1021 Codex round 2: every fetch takes a number in the order it was SENT,
 * and a response applies only if it is newer than the last one applied. An
 * exported refresh() in flight while a watched-store change starts another
 * fetch can land second; without this, its rows overwrite the newer ring or
 * incoming call with nulls until a later poll. Newer-than-applied rather than
 * equal-to-latest-sent: an older response landing while the newer is still
 * in flight (or after the newer failed) is still the freshest the store has.
 */
let fetchSeq = 0;
let appliedSeq = 0;
let staleResponsesDropped = 0;

/** Debug: how many out-of-order responses were dropped (tests, diagnostics). */
export function activeCohortFeedDiag(): { staleResponsesDropped: number } {
  return { staleResponsesDropped };
}

/** Invalidate the running loop and any in-flight response. */
function invalidate(): void {
  generation += 1;
  // The anchor belongs to the loop it was measured in.
  lastFetchStartedAt = 0;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

export const useActiveCohortSessionsStore = create<ActiveCohortSessionsState>((set, get) => ({
  sessions: [],
  ring: null,
  notice: null,
  availableNow: false,
  incomingCall: null,
  missedCall: null,
  missedDismissed: {},
  startingSoon: null,
  serverClockOffsetMs: 0,
  dismissMissed: () =>
    set((prev) => ({
      missedCall: null,
      missedDismissed: prev.missedCall
        ? { ...prev.missedDismissed, [prev.missedCall.session_id]: true }
        : prev.missedDismissed,
    })),
  refresh: async () => {
    // Belt and braces with the hook's gate below: refresh() is exported for
    // out-of-band use (leaving a circle calls it), and those callers should not
    // each have to remember this. The route is inside the authenticated API
    // group, so an unauthenticated call is a guaranteed 401 and nothing else.
    if (!useAuthStore.getState().isAuthenticated) return;

    const startedAt = generation;
    const sentAt = Date.now();
    const seq = ++fetchSeq;
    try {
      const feed = await getActiveCohortFeed();
      // Logged out, switched accounts, or the loop was superseded while this
      // was in flight — the rows describe someone who is no longer here.
      if (startedAt !== generation) return;
      // A newer response already landed: these rows are older than the store's.
      if (seq <= appliedSeq) {
        staleResponsesDropped += 1;
        return;
      }
      appliedSeq = seq;
      // Before the set(): the set() is what tells reconsiderCadence to look.
      lastFetchStartedAt = Math.max(lastFetchStartedAt, sentAt);
      set((prev) => ({
        sessions: feed.sessions,
        ring: feed.ring,
        notice: feed.notice,
        availableNow: feed.availableNow,
        incomingCall: feed.incomingCall ?? null,
        missedCall:
          feed.missedCall && !prev.missedDismissed[feed.missedCall.session_id]
            ? feed.missedCall
            : prev.missedCall,
        startingSoon: feed.startingSoon ?? null,
        serverClockOffsetMs: feed.startingSoon
          ? Date.parse(feed.startingSoon.server_time) - Date.now()
          : prev.serverClockOffsetMs,
      }));
    } catch {
      /* non-critical: consumers simply render nothing live */
      // Still the newest attempt: anchoring to an older one would let every
      // input change re-fetch at once while the network is down.
      if (startedAt === generation) lastFetchStartedAt = Math.max(lastFetchStartedAt, sentAt);
    }
  },
  pollNow: () => {
    if (subscribers > 0) {
      invalidate();
      tick(generation);
      return;
    }
    void get().refresh();
  },
  reset: () => {
    invalidate();
    set({
      sessions: [],
      ring: null,
      notice: null,
      availableNow: false,
      incomingCall: null,
      missedCall: null,
      missedDismissed: {},
      startingSoon: null,
      serverClockOffsetMs: 0,
    });
  },
}));

function tick(myGeneration: number): void {
  void useActiveCohortSessionsStore
    .getState()
    .refresh()
    .finally(() => {
      // Re-arm only while somebody is still listening AND this is still the
      // live loop. A consumer can unmount, or the identity can change, while
      // the request is in flight.
      if (subscribers > 0 && myGeneration === generation) {
        arm(myGeneration, nextPollDelay(useActiveCohortSessionsStore.getState()));
      }
    });
}

function arm(myGeneration: number, delay: number): void {
  armedDeadline = Date.now() + delay;
  timer = setTimeout(() => fire(myGeneration), delay);
}

/** The armed timer is spent once it fires: an in-flight tick has no timer to move. */
function fire(myGeneration: number): void {
  timer = null;
  tick(myGeneration);
}

/**
 * The cadence is a function of the CURRENT state, not of the state when the
 * timer was armed. nextPollDelay's inputs change between ticks, out of band:
 *
 *  - the feed's own ring / incomingCall / availableNow, rewritten by every
 *    refresh() a caller runs outside the cadence (availability.service,
 *    RingSheet, IncomingCallSheet, CohortEmpathyTab, AvailabilityNoticeToast);
 *  - the ring and incoming-call handled maps (RingSheet's restore() gives a
 *    ring back when a decline never reached the server);
 *  - the seeker's state (a request going up, or a claim the seat poll has
 *    already seen: preJoinSeat sets WAITING).
 *
 * Any of them can shorten the cadence after a 30 s timer was armed, and
 * waiting that out breaks "within one short poll" (R11, P7d). So on every
 * change to any of those stores, the armed timer is moved to one short
 * interval after the newest fetch was sent (at once if that is already past).
 * Only an EARLIER deadline moves it; the loop stays one loop (same
 * generation), and an in-flight tick needs nothing: it arms from the current
 * state when it lands. With no timer armed (no poll running, or a tick in
 * flight) there is nothing to move: the timer only exists while somebody is
 * subscribed (tick re-arms only then; the last unsubscribe clears it).
 */
function reconsiderCadence(): void {
  if (timer === null) return;
  const deadline = lastFetchStartedAt + nextPollDelay(useActiveCohortSessionsStore.getState());
  if (deadline >= armedDeadline) return;
  clearTimeout(timer);
  const myGeneration = generation;
  armedDeadline = deadline;
  timer = setTimeout(() => fire(myGeneration), Math.max(0, deadline - Date.now()));
}

/**
 * The server's "your incoming call changed, ask now" (IncomingCallChanged, on
 * the private `user.{id}` channel): a listener claimed the request (`ringing`),
 * or the call was voided or reached on another device (`closed`). The
 * handler ignores the payload and polls NOW; the feed's `incoming_call` stays
 * the only thing that opens or closes a sheet, so a lost, late or stray event
 * costs one extra fetch and nothing else.
 *
 * Listened to for as long as the poll runs (first subscriber to last), as
 * whoever is signed in when it starts (an identity change restarts the loop).
 * The poll keeps its cadence underneath: with the socket off
 * (VITE_WS_ENABLED unset, an e2e stack with BROADCAST_CONNECTION=null, a
 * mobile build shipped without WS) nothing here runs and the poll alone
 * delivers the call, as before.
 */
export const INCOMING_CALL_EVENT = '.incoming_call.changed';

let stopIncomingCallSocket: (() => void) | null = null;

function listenForIncomingCalls(): void {
  unlistenForIncomingCalls();
  if (!isWsEnabled()) return;
  const userId = useAuthStore.getState().user?.id;
  if (!userId) return;
  const onChanged = () => useActiveCohortSessionsStore.getState().pollNow();
  try {
    // Held, not looked up again on the way out: after logout destroys the
    // Echo instance, getEcho() would open a new socket just to unsubscribe.
    const channel = getEcho().private(`user.${userId}`);
    channel.listen(INCOMING_CALL_EVENT, onChanged);
    // stopListening WITH the handler: other components share this channel,
    // and without it every listener for the event goes.
    stopIncomingCallSocket = () => {
      try {
        channel.stopListening(INCOMING_CALL_EVENT, onChanged);
      } catch {
        /* the socket went with a logout */
      }
    };
  } catch {
    /* no socket: the poll alone, as with WS off */
  }
}

function unlistenForIncomingCalls(): void {
  stopIncomingCallSocket?.();
  stopIncomingCallSocket = null;
}

/** Watching every cadence input for as long as the loop runs (first subscriber to last). */
let stopWatchingInputs: Array<() => void> = [];

function watchCadenceInputs(): void {
  unwatchCadenceInputs();
  stopWatchingInputs = [
    useActiveCohortSessionsStore.subscribe(reconsiderCadence),
    useRingStore.subscribe(reconsiderCadence),
    useIncomingCallStore.subscribe(reconsiderCadence),
    useEmpathyQueueStore.subscribe(reconsiderCadence),
  ];
}

function unwatchCadenceInputs(): void {
  for (const stop of stopWatchingInputs) stop();
  stopWatchingInputs = [];
}

/**
 * Subscribe to the shared poll for as long as the component is mounted.
 *
 * `enabled` lets a consumer opt out without breaking the rules of hooks —
 * CircleStartBanner suppresses it during a live session, where the banner
 * cannot render anyway and the data would be thrown away.
 */
export function useActiveCohortSessions(enabled = true): ActiveCohortSession[] {
  const sessions = useActiveCohortSessionsStore((s) => s.sessions);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Not just "is someone signed in" — WHO. Switching accounts in a live SPA
  // has to invalidate the previous account's rows.
  const userId = useAuthStore((s) => s.user?.id ?? null);

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      // Drop whatever the previous identity saw, so signing out (or a guest
      // landing on a warm store) never shows someone else's live circles.
      useActiveCohortSessionsStore.getState().reset();
      return;
    }

    subscribers += 1;
    if (subscribers === 1) {
      // A fresh loop for a fresh identity: supersede anything still in flight
      // from the previous one before starting.
      invalidate();
      watchCadenceInputs();
      listenForIncomingCalls();
      tick(generation);
    }

    return () => {
      subscribers -= 1;
      if (subscribers === 0) {
        invalidate();
        unwatchCadenceInputs();
        unlistenForIncomingCalls();
      }
    };
  }, [enabled, isAuthenticated, userId]);

  return sessions;
}

// Per-user, with its own poll loop: resetting also supersedes any in-flight
// response. Reset synchronously on every session change and on entering the
// guest tour (#940 r9).
registerUserScopedReset(() => useActiveCohortSessionsStore.getState().reset());
